"use client";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import React from "react";
import type { AnimationItem } from "lottie-web";

const LottieAnimation = ({
  className,
  loop = false,
  animation,
  autoplay = true,
}: ILottieAnimation) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = React.useState(false);

  React.useEffect(() => {
    if (!containerRef.current) return;

    let anim: AnimationItem | undefined;
    let cancelled = false;
    let cleanupObserver: (() => void) | undefined;
    setIsLoaded(false);

    const container = containerRef.current;

    // Loaded on demand instead of bundled at module scope - this component
    // renders in the hero section on nearly every page, so eagerly importing
    // lottie-web shipped its full weight in every page's critical JS. Also
    // pulled from the SVG-only build (not the default "all renderers" one) -
    // we only ever request renderer: "svg" below, so the canvas/html renderer
    // code in the default build was dead weight (this alone was measured at
    // ~2.5s of script evaluation on the homepage, the single largest cost in
    // the whole page).
    //
    // Tried switching this to lottie-web's canvas renderer at one point to
    // work around the SVG-renderer DOM leak below (canvas draws pixels, not
    // DOM nodes, so there's nothing for that leak to accumulate) - measured
    // it actually fixing the leak (DOM size 10,187 -> 888 on the homepage),
    // but it made continuous per-frame canvas redraw a main-thread cost for
    // the animation's entire lifetime instead of a one-time layout cost,
    // which pushed Total Blocking Time from 410ms to 2,760ms - a much worse
    // regression than the leak it fixed. Capping the loop count below instead
    // keeps the cheaper SVG renderer while still bounding the leak.
    const load = () => {
      import("lottie-web/build/player/lottie_svg").then(
        ({ default: lottie }) => {
          if (cancelled || !containerRef.current) return;
          anim = lottie.loadAnimation({
            container: containerRef.current,
            renderer: "svg",
            loop,
            autoplay,
            path: `/animations_json/${animation.toLowerCase()}.json`,
            rendererSettings: {
              preserveAspectRatio: "xMidYMid slice",
            },
          });

          // lottie-web's SVG renderer has a long-standing upstream memory leak
          // (airbnb/lottie-web#1159, #2070, #3079): shape/mask/gradient elements
          // are rebuilt every frame instead of updated in place, so a looping
          // animation's DOM keeps growing without bound - measured here at
          // 900+ new SVG nodes every 2 seconds, never plateauing. There's no
          // newer lottie-web release that fixes it (5.13.0 is current). Since
          // these are decorative, aria-hidden background animations, freezing on
          // the final frame after a bounded number of loops is an acceptable
          // trade-off: it caps the leak's total size instead of eliminating the
          // animation or paying canvas's ongoing CPU cost to avoid it entirely.
          const MAX_LOOPS = 20;
          let completedLoops = 0;
          if (loop) {
            anim.addEventListener("loopComplete", () => {
              completedLoops += 1;
              if (completedLoops >= MAX_LOOPS) {
                anim?.goToAndStop(anim.totalFrames - 1, true);
              }
            });
          }

          // lottie-web's SVG renderer unconditionally sets `aria-label` on each
          // text layer's <g> (see lottie_svg.js SVGTextLayer#buildTextContents),
          // which is an invalid attribute for an element with no naming-capable
          // ARIA role. This animation has multiple text layers that activate at
          // different points in the loop, so `aria-label` keeps getting re-added
          // as each layer becomes active - a one-shot cleanup after DOMLoaded
          // misses everything after the first frame. Observe the whole animation
          // lifetime instead, since the animation is decorative anyway (see
          // aria-hidden on the container below).
          const mutationObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
              if (mutation.type === "attributes") {
                (mutation.target as Element).removeAttribute("aria-label");
              } else {
                // Covers nodes created with aria-label already set before being
                // inserted - an attribute-only observer never sees those, since
                // the attribute isn't "changed" once the node is already tracked.
                mutation.addedNodes.forEach((node) => {
                  if (!(node instanceof Element)) return;
                  node.removeAttribute("aria-label");
                  node
                    .querySelectorAll("[aria-label]")
                    .forEach((el) => el.removeAttribute("aria-label"));
                });
              }
            }
          });
          mutationObserver.observe(containerRef.current, {
            attributes: true,
            attributeFilter: ["aria-label"],
            childList: true,
            subtree: true,
          });

          anim.addEventListener("DOMLoaded", () => {
            setIsLoaded(true);
          });

          cleanupObserver = () => mutationObserver.disconnect();
        },
      );
    };

    // Below-the-fold instances (feature cards, signup section, etc.) used to
    // start loading lottie-web and building the SVG animation tree the
    // instant they mounted, which is well before the user ever scrolls to
    // them - that ~2s of script work still landed inside the initial page
    // load's Total Blocking Time window. Gating the load behind
    // IntersectionObserver defers that cost until the animation is actually
    // about to be seen; an above-the-fold instance (e.g. the homepage hero)
    // intersects immediately, so this doesn't delay those at all.
    let intersectionObserver: IntersectionObserver | undefined;
    if (typeof IntersectionObserver !== "undefined") {
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            intersectionObserver?.disconnect();
            load();
          }
        },
        { rootMargin: "200px" },
      );
      intersectionObserver.observe(container);
    } else {
      load();
    }

    return () => {
      cancelled = true;
      intersectionObserver?.disconnect();
      cleanupObserver?.();
      anim?.destroy();
    };
  }, [animation, loop, autoplay]);

  return (
    <div className={cn("relative w-full h-full", className)} aria-hidden="true">
      {!isLoaded && (
        <div className="absolute inset-0 flex justify-center items-center">
          <Loader2 className="animate-spin" />
        </div>
      )}
      <div ref={containerRef} className="w-full h-full" />
    </div>
  );
};

export default LottieAnimation;
